import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '@/utils/api';
import type { SkillTrack, SkillTrackProgress, TrackDifficulty, TrackCategory } from '@/types';

interface TracksFilters {
  category?: TrackCategory;
  difficulty?: TrackDifficulty;
  search?: string;
  isOfficial?: boolean;
}

interface TracksState {
  tracks: SkillTrack[];
  currentTrack: SkillTrack | null;
  myProgress: SkillTrackProgress[];
  filters: TracksFilters;
  isLoading: boolean;
  error: string | null;
}

const initialState: TracksState = {
  tracks: [],
  currentTrack: null,
  myProgress: [],
  filters: {},
  isLoading: false,
  error: null,
};

export const fetchTracks = createAsyncThunk(
  'tracks/fetchTracks',
  async (filters: TracksFilters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.set('category', filters.category);
      if (filters.difficulty) params.set('difficulty', filters.difficulty);
      if (filters.search) params.set('search', filters.search);
      if (filters.isOfficial !== undefined) params.set('isOfficial', String(filters.isOfficial));

      const response = await api.get(`/tracks?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch tracks'
      );
    }
  }
);

export const fetchTrack = createAsyncThunk(
  'tracks/fetchTrack',
  async (trackId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tracks/${trackId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch track'
      );
    }
  }
);

export const fetchMyProgress = createAsyncThunk(
  'tracks/fetchMyProgress',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/tracks/my/progress');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch progress'
      );
    }
  }
);

export const startTrack = createAsyncThunk(
  'tracks/startTrack',
  async (trackId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tracks/${trackId}/start`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to start track'
      );
    }
  }
);

export const completeTrackTask = createAsyncThunk(
  'tracks/completeTrackTask',
  async ({ trackId, taskId }: { trackId: string; taskId: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tracks/${trackId}/complete-task`, { taskId });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to complete task'
      );
    }
  }
);

const tracksSlice = createSlice({
  name: 'tracks',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<TracksFilters>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentTrack: (state, action: PayloadAction<SkillTrack | null>) => {
      state.currentTrack = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tracks
      .addCase(fetchTracks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTracks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tracks = action.payload;
      })
      .addCase(fetchTracks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch single track
      .addCase(fetchTrack.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTrack.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTrack = action.payload;
      })
      .addCase(fetchTrack.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch my progress
      .addCase(fetchMyProgress.fulfilled, (state, action) => {
        state.myProgress = action.payload;
      })
      // Start track
      .addCase(startTrack.fulfilled, (state, action) => {
        // Update the track as enrolled
        const track = state.tracks.find((t) => t.id === action.payload.trackId);
        if (track) {
          track.isEnrolled = true;
          track.userProgress = 0;
          track.userStatus = 'in_progress';
        }
        if (state.currentTrack && state.currentTrack.id === action.payload.trackId) {
          state.currentTrack.isEnrolled = true;
          state.currentTrack.userProgress = 0;
          state.currentTrack.userStatus = 'in_progress';
        }
      })
      // Complete task
      .addCase(completeTrackTask.fulfilled, (state, action) => {
        const { trackId, tasksCompleted, totalTasks, status } = action.payload;
        const progress = (tasksCompleted / totalTasks) * 100;

        const track = state.tracks.find((t) => t.id === trackId);
        if (track) {
          track.userProgress = progress;
          track.userStatus = status;
        }
        if (state.currentTrack && state.currentTrack.id === trackId) {
          state.currentTrack.userProgress = progress;
          state.currentTrack.userStatus = status;
        }

        // Update myProgress
        const progressIdx = state.myProgress.findIndex((p) => p.trackId === trackId);
        if (progressIdx !== -1) {
          state.myProgress[progressIdx].tasksCompleted = tasksCompleted;
          state.myProgress[progressIdx].progressPercent = progress;
          state.myProgress[progressIdx].status = status;
        }
      });
  },
});

export const { setFilters, clearFilters, setCurrentTrack } = tracksSlice.actions;
export default tracksSlice.reducer;
